import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { getAdvisorReview, getAdvisorReviewTranscript, approveAdvisorReview, recoverAdvisorReview } from '../../services/appointmentsApi';
import { hasReviewAccess, reviewIdentity, isPendingReview, reviewIsLive, priceToMinor, formatMinor, formatEstimate, formatReviewDate, finalMessagePreview, reviewErrorKey,
  ADICIONALES_LIMIT, ADICIONAL_DESCRIPCION_MAX, emptyAdicional, adicionalesToRequest, adicionalesMinor, totalMinor } from './advisorReview';

import { branchLabel, deriveSucursalKey, SUCURSAL_UNKNOWN } from './calendarTags';
import { formatBookingExtras } from './bookingExtras';
import DatosDisputeMarker from './DatosDisputeMarker';
import { formatDatosUpdates } from './datosUpdates';

import './advisorReview.css';

// P1 postventa cleanup (oct22): the booking context names the sucursal the way the Calendar
// chips do (#2214: GCGMVILLA -> "Villahermosa: Deportiva"; Jocelin oct22 / #2229: a
// requested_site Tabasco 2000 has no taller and folds into "Villahermosa: Deportiva" too);
// an unknown branch keeps the row's own label. Display only —
// the customer-facing WhatsApp preview (finalMessagePreview) is untouched.
export function reviewSucursalText(row) {
  if (!row) return '';
  const key = deriveSucursalKey(row);
  if (key && key !== SUCURSAL_UNKNOWN) {
    const label = branchLabel(key);
    if (label && label !== key) return label;
  }
  return String(row.sucursal || row.location_id || '').trim();
}

const buttonClass = 'rounded border px-3 py-2 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-emerald-600';
export default function AdvisorReviewPanel({ appointmentId, user, onClose, onChanged, onBack }) {
  const identity = reviewIdentity(user);
  // td244: the preview names the LOGGED-IN approver (server renders the same from the user row).
  const approverName = String(user?.name || user?.full_name || '').trim();
  return <ReviewSession key={JSON.stringify([appointmentId, identity])} appointmentId={appointmentId}
    identity={identity} approverName={approverName} onClose={onClose} onChanged={onChanged} onBack={onBack} />;
}

function ReviewSession({ appointmentId, identity, approverName, onClose, onChanged, onBack }) {
  const user = useMemo(() => identity ? JSON.parse(identity) : null, [identity]);
  const { t, i18n } = useTranslation();
  const label = (key) => t('advisorReview.' + key);
  const [detail, setDetail] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [evidenceError, setEvidenceError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState('');
  // Oct 19 (client call): adicionales line items the advisor adds BEFORE confirming; local until
  // Aceptar sends them with the base price and the server persists them in review_final.payload.
  const [adicionales, setAdicionales] = useState([]);
  const [reload, setReload] = useState(0);
  const [locked, setLocked] = useState(false);
  const inFlight = useRef(null);
  const generation = useRef(0);
  // Invalidate during unmount commit, before an obsolete promise can notify Calendar.
  useLayoutEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => {
    const current = ++generation.current;
    setLoading(true); setDetail(null); setEvidence(null); setError(''); setEvidenceError('');
    setPrice(''); setAdicionales([]); setLocked(false); setBusy(false); inFlight.current = null;
    if (!hasReviewAccess(user)) { setError('denied'); setLoading(false); return undefined; }
    const active = () => generation.current === current;
    getAdvisorReview(appointmentId).then(({ data }) => {
      if (!active()) return;
      if (!data?.appointment || data.appointment.appointment_id !== appointmentId
          || !hasReviewAccess(user, data.appointment) || data.appointment.advisor_review_enrolled !== true) {
        setError('denied'); return;
      }
      setDetail(data);
      // This is the only transcript request; there is deliberately no fallback.
      getAdvisorReviewTranscript(appointmentId).then(({ data: transcript }) => {
        if (active()) setEvidence(transcript);
      }).catch((err) => { if (active()) setEvidenceError(reviewErrorKey(err)); });
    }).catch((err) => { if (active()) setError(reviewErrorKey(err)); })
      .finally(() => { if (active()) setLoading(false); });
    return () => { generation.current += 1; };
  }, [appointmentId, user, reload]);

  const row = detail?.appointment;
  const approved = row?.advisor_review_status === 'approved';
  const baseMinor = priceToMinor(price);
  // null = at least one filled line is invalid (never silently dropped); [] = no adicionales.
  const requestItems = adicionalesToRequest(adicionales);
  const extrasMinor = adicionalesMinor(requestItems);
  // The amount the final WhatsApp renders: base + sum(adicionales) (mirrors review.total_minor).
  const minor = totalMinor(baseMinor, requestItems);
  const hasAdicionales = Array.isArray(requestItems) && requestItems.length > 0;
  const approvedPayload = approved ? row.review_final?.payload : null;
  const approvedAdicionales = Array.isArray(approvedPayload?.adicionales) ? approvedPayload.adicionales : [];
  const canApprove = row && detail.can_approve === true && isPendingReview(row)
    && hasReviewAccess(user, row) && !locked && Boolean(evidence || evidenceError) && evidenceError !== 'denied';
  const preview = approved ? row.review_final?.payload?.text : row && finalMessagePreview(row, minor, approverName, hasAdicionales);
  const editAdicional = (index, field, value) => setAdicionales((items) => items.map((item, i) => i === index ? { ...item, [field]: value } : item));
  const removeAdicional = (index) => setAdicionales((items) => items.filter((_, i) => i !== index));
  const addAdicional = () => setAdicionales((items) => items.length < ADICIONALES_LIMIT ? [...items, emptyAdicional()] : items);
  // td223 Roger spec (oct07): the card names WHAT is being priced (service · vehicle ·
  // booked slot · branch · advisor) and lets the advisor CONFIRM the call estimate with
  // one click (copies it into the editable final-price field) or EDIT it by typing.
  const bookingContext = row ? [row.service, row.vehicle, formatReviewDate(row.start_datetime, i18n?.language),
    reviewSucursalText(row), row.assigned_advisor].filter(Boolean).join(' · ') : '';
  // taxi-factura-oct20: the customer's optional taxi / factura answers (strict booleans on the
  // row) so the advisor pricing the cita sees them; '' when the customer was never asked.
  const bookingExtras = row ? formatBookingExtras(row, (k, f) => t(k, f)) : '';
  // td453: the customer's datos corrections (WhatsApp reply / voice readback) -> the advisor sees
  // what changed on the record (applied) or what to review (serie), '' when nothing was corrected.
  const datosUpdates = row ? formatDatosUpdates(row, (k, f) => t(k, f)) : '';
  const estimateValue = detail && !detail.estimate_unavailable && formatEstimate(detail.estimate, 'en') ? String(detail.estimate) : '';
  const canUseEstimate = Boolean(estimateValue) && canApprove && !approved && priceToMinor(estimateValue) !== null;
  async function mutate(kind) {
    if (inFlight.current || !row || locked || evidenceError === 'denied' || (kind === 'approve' && (!canApprove || minor === null))) return;
    const request = {};
    inFlight.current = request; setBusy(true); setError('');
    const current = generation.current;
    try {
      // price_minor = BASE price; the server computes and persists the total (base + adicionales).
      const response = kind === 'approve'
        ? await approveAdvisorReview(appointmentId, { price_minor: baseMinor, expected_version: row.review_version, adicionales: requestItems })
        : await recoverAdvisorReview(appointmentId, { event: kind, expected_version: row.review_version });
      if (generation.current !== current) return;
      const next = response.data;
      if (!next?.appointment || next.appointment.appointment_id !== appointmentId || !hasReviewAccess(user, next.appointment)) {
        setLocked(true); setError('requestFailed'); return;
      }
      setDetail(next);
      if (kind === 'approve' && next.appointment.advisor_review_status !== 'approved') {
        setLocked(true); setError('requestFailed');
      }
      onChanged?.(next.appointment);
    } catch (err) {
      if (generation.current !== current) return;
      setError(reviewErrorKey(err));
      // A lost response may hide durable approval. Re-read before any new action.
      setLocked(true);
    } finally {
      // An obsolete completion never releases a newer request's click guard.
      if (inFlight.current === request) {
        inFlight.current = null;
        if (generation.current === current) setBusy(false);
      }
    }
  }
  const deliveryIssues = row ? ['pending', 'final'].filter((kind) => {
    const event = row['review_' + kind];
    if (!event) return false;
    const issue = ['blocked', 'failed', 'undelivered', 'claimed', 'potentially_attempted', 'uncertain'].includes(event.state)
      || ['failed', 'undelivered'].includes(event.provider_status);
    // Normal automatic post-call delivery is not part of the advisor's work.
    return issue || (kind === 'pending' && approved && !event.message_sid)
      || (kind === 'final' && approved && event.state === 'ready');
  }) : [];
  return <Dialog open onClose={() => { if (!busy) onClose(); }} className="relative z-50">
    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
    <div className="fixed inset-0 overflow-y-auto p-4 sm:p-8">
      <Dialog.Panel className="review-panel mx-auto max-w-4xl">
        <div className="review-panel-heading flex items-center justify-between gap-3">
          {onBack && <button type="button" className={buttonClass} disabled={busy} onClick={onBack}>{label('backToList')}</button>}
          <Dialog.Title className="min-w-0 flex-1 text-lg font-semibold">{label('title')}<span className="review-muted mt-1 block text-sm font-normal">{row?.customer_name}</span>
            {bookingContext && <span className="review-muted mt-1 block text-sm font-normal" data-testid="review-booking-context">{bookingContext}</span>}
            {bookingExtras && <span className="review-muted mt-1 block text-sm font-normal" data-testid="review-booking-extras">{bookingExtras}</span>}
            {datosUpdates && <span className="review-muted mt-1 block text-sm font-normal" data-testid="review-datos-updates">{datosUpdates}</span>}</Dialog.Title>
          <button className={buttonClass} type="button" onClick={onClose} disabled={busy}>{label('close')}</button>
        </div>
        {loading && <p role="status">{label('loading')}</p>}
        {error && <p role="alert" className="my-3 text-red-700 dark:text-red-300">{label(error)}</p>}
        {!loading && (error || locked || evidenceError || deliveryIssues.length > 0) && <button className={buttonClass + ' my-3'} type="button" disabled={busy} onClick={() => setReload((n) => n + 1)}>{label('refreshDetail')}</button>}
        {/* datos step B oct21: the customer disputed a datum on WhatsApp -> the advisor sees what and
            the customer's words, corrects it in B.PRO and marks it atendido (never a review mutation). */}
        {row && <DatosDisputeMarker row={row} testId="review-datos-dispute"
          onChanged={(next) => { setDetail((d) => (d ? { ...d, appointment: { ...d.appointment, ...next } } : d)); onChanged?.(next); }} />}
        {row && <>
          <div className="review-content-grid"><div className="review-transcript-column">
          <section aria-label={label('transcript')} className="review-transcript">
            <h3 className="font-semibold">{label('transcript')}</h3>
            {evidenceError ? <p role="alert">{label(evidenceError)}</p>
              : !evidence ? <p role="status">{label('loadingEvidence')}</p>
                : !evidence.available ? <p role="status">{label('evidenceUnavailable')}</p>
                  : <div className="review-conversation overflow-y-auto whitespace-pre-wrap" tabIndex={0} aria-label={label('transcript')}>
                    {(Array.isArray(evidence.entries) ? evidence.entries : []).map((entry, i) => <p key={i}>
                      {typeof entry === 'string' ? entry : [entry.speaker || entry.role, entry.text || entry.content].filter(Boolean).join(': ')}
                    </p>)}
                  </div>}
          </section>
          </div><div className="review-decision-column">
          <p className="review-estimate">{label(detail.estimate_source === 'catalog_reference' ? 'catalogEstimate' : 'estimate')}<strong>{detail.estimate == null || detail.estimate_unavailable ? label('unavailable') : formatEstimate(detail.estimate, i18n?.language) || label('unavailable')}</strong></p>
          <p role="status" className={approved ? "review-status review-success" : "review-status"}>{label(approved ? 'approved' : isPendingReview(row) ? 'pending' : 'notReviewable')}</p>
          {approved ? <div data-testid="review-approved-total">
              <p>{label('approvedPrice')}: {formatMinor(approvedPayload?.price_minor)} MXN</p>
              {approvedAdicionales.length > 0 && <section aria-label={label('adicionales.title')} className="review-adicionales mt-2">
                <p className="text-sm">{label('adicionales.base')}: {formatMinor(approvedPayload?.base_price_minor)} MXN</p>
                <ul className="text-sm">
                  {approvedAdicionales.map((item, i) => <li key={i} data-testid="review-adicional-approved">{item.descripcion}: {formatMinor(item.precio_minor)} MXN</li>)}
                </ul>
                <p className="text-sm">{label('adicionales.sum')}: {formatMinor(approvedPayload?.adicionales_minor)} MXN</p>
              </section>}
            </div>
            : <form onSubmit={(e) => { e.preventDefault(); mutate('approve'); }} className="my-3 space-y-2">
              <label htmlFor="advisor-final-price" className="block font-medium">{label('price')}</label>
              <input id="advisor-final-price" type="text" inputMode="decimal" autoComplete="off" value={price}
                disabled={!canApprove || busy} onChange={(e) => setPrice(e.target.value)}
                aria-invalid={price !== '' && baseMinor === null} aria-describedby="advisor-price-help"
                className="review-price-input" placeholder="0.00" />
              <p id="advisor-price-help" className="text-sm">{label(baseMinor === null && price !== '' ? 'invalid' : 'priceHelp')}</p>
              {canUseEstimate && <button type="button" className={buttonClass} disabled={busy}
                onClick={() => setPrice(estimateValue)}>{label('useEstimate')}</button>}
              <section aria-label={label('adicionales.title')} className="review-adicionales" data-testid="review-adicionales">
                <h3 className="font-semibold">{label('adicionales.title')}</h3>
                <p className="text-sm">{label('adicionales.help')}</p>
                {adicionales.map((item, index) => <div key={index} className="review-adicional-row flex flex-wrap items-center gap-2" data-testid="review-adicional-row">
                  <input type="text" autoComplete="off" maxLength={ADICIONAL_DESCRIPCION_MAX} value={item.descripcion}
                    aria-label={label('adicionales.descripcion') + ' ' + (index + 1)} placeholder={label('adicionales.descripcionPlaceholder')}
                    disabled={!canApprove || busy} onChange={(e) => editAdicional(index, 'descripcion', e.target.value)}
                    className="review-price-input flex-1" />
                  <input type="text" inputMode="decimal" autoComplete="off" value={item.precio}
                    aria-label={label('adicionales.precio') + ' ' + (index + 1)} placeholder="0.00"
                    aria-invalid={item.precio !== '' && priceToMinor(item.precio) === null}
                    disabled={!canApprove || busy} onChange={(e) => editAdicional(index, 'precio', e.target.value)}
                    className="review-price-input w-32" />
                  <button type="button" className={buttonClass} disabled={busy} aria-label={label('adicionales.remove') + ' ' + (index + 1)}
                    onClick={() => removeAdicional(index)}>{label('adicionales.remove')}</button>
                </div>)}
                <button type="button" className={buttonClass} disabled={!canApprove || busy || adicionales.length >= ADICIONALES_LIMIT}
                  onClick={addAdicional}>{label('adicionales.add')}</button>
                {requestItems === null && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{label('adicionales.invalid')}</p>}
                {hasAdicionales && baseMinor !== null && <p className="text-sm" data-testid="review-total-breakdown">
                  {label('adicionales.base')}: {formatMinor(baseMinor)} MXN · {label('adicionales.sum')}: {formatMinor(extrasMinor)} MXN · {label('adicionales.total')}: {minor === null ? label('adicionales.overLimit') : formatMinor(minor) + ' MXN'}
                </p>}
              </section>
              <h3 className="font-semibold">{label('preview')}</h3>
              <p className="review-whatsapp whitespace-pre-wrap">{preview || label(minor === null ? 'enterPrice' : 'previewBlocked')}</p>
              <button type="submit" className="review-accept" disabled={!canApprove || minor === null || busy}>{label(busy ? 'submitting' : 'accept')}</button>

            </form>}
          {approved && <section className="my-3"><h3 className="font-semibold">{label('finalMessage')}</h3><p className="whitespace-pre-wrap">{preview || label('previewBlocked')}</p></section>}
          {deliveryIssues.length > 0 && <section aria-label={label('delivery')} className="review-delivery mt-4 border-t pt-3">
            <h3 className="font-semibold">{label('deliveryAttention')}</h3>
            {deliveryIssues.map((kind) => {
              const event = row['review_' + kind];
              const safeDispatch = ['ready', 'blocked'].includes(event.state);
              const canRecover = reviewIsLive(row) && (safeDispatch || Boolean(event.message_sid));
              return <div key={kind} className="my-3">
                <h4 className="text-sm font-medium">{label(kind === 'pending' ? 'pendingDependency' : 'finalNotice')}</h4>
                <p className="text-sm">{label('states.' + (event.provider_status === 'failed' || event.provider_status === 'undelivered' ? event.provider_status : event.state))}</p>
                {event.reason?.startsWith('missing_final_fields:') && <p className="text-sm">{label('missingDetails')}</p>}
                {canRecover ? <button type="button" className={buttonClass + ' mt-2'} disabled={busy || locked || !Boolean(evidence || evidenceError) || evidenceError === 'denied'}
                  onClick={() => mutate(kind)}>{label(safeDispatch ? 'recover' : 'reconcile')}</button>
                  : ['claimed', 'potentially_attempted', 'uncertain'].includes(event.state) && <p className="text-sm">{label('noResend')}</p>}
              </div>;
            })}
          </section>}
          </div></div>
        </>}
      </Dialog.Panel>
    </div>
  </Dialog>;
}
